// Steering force terms for the bubble field. Each function returns a
// Vec2 acceleration (already clamped to its own maxForce). The flow
// term lives in bubble-flow.ts; the four corrections layered on top
// of it are here.

import type { Bubble, Rect, Vec2 } from './bubble-physics';
import {
  CURSOR_FORCE,
  CURSOR_RADIUS,
  LOOKAHEAD,
  ORBIT_FORCE,
  UI_AVOID_DEPTH_MAX,
  UI_AVOID_DEPTH_MIN,
  UI_AVOID_FORCE,
  UI_BUFFER,
} from './bubble-constants';

export function steerToward(desired: Vec2, vel: Vec2, max: number): Vec2 {
  const dx = desired.x - vel.x;
  const dy = desired.y - vel.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-4) return { x: 0, y: 0 };
  const m = Math.min(len, max);
  return { x: (dx / len) * m, y: (dy / len) * m };
}

function avoidOne(b: Bubble, r: Rect): Vec2 {
  const ax = b.pos.x + b.vel.x * LOOKAHEAD + b.size / 2;
  const ay = b.pos.y + b.vel.y * LOOKAHEAD + b.size / 2;
  const s =
    UI_AVOID_DEPTH_MIN + (UI_AVOID_DEPTH_MAX - UI_AVOID_DEPTH_MIN) * b.depth;
  const buf = UI_BUFFER * s + b.size / 2;
  const cx = Math.max(r.x, Math.min(ax, r.x + r.w));
  const cy = Math.max(r.y, Math.min(ay, r.y + r.h));
  const dx = ax - cx;
  const dy = ay - cy;
  const d = Math.hypot(dx, dy);
  if (d >= buf) return { x: 0, y: 0 };
  const t = 1 - d / buf;
  const nx = d > 0.01 ? dx / d : 1;
  const ny = d > 0.01 ? dy / d : 0;
  const f = UI_AVOID_FORCE * s * t;
  return { x: nx * f, y: ny * f };
}

export function uiAvoid(b: Bubble, anchors: Rect[]): Vec2 {
  let fx = 0;
  let fy = 0;
  for (let i = 0; i < anchors.length; i++) {
    const r = anchors[i];
    if (!r) continue;
    const f = avoidOne(b, r);
    fx += f.x;
    fy += f.y;
  }
  return { x: fx, y: fy };
}

export function cursorFlee(b: Bubble, c: Vec2): Vec2 {
  const dx = b.pos.x + b.size / 2 - c.x;
  const dy = b.pos.y + b.size / 2 - c.y;
  const d = Math.hypot(dx, dy);
  if (d >= CURSOR_RADIUS || d < 1) return { x: 0, y: 0 };
  const t = 1 - d / CURSOR_RADIUS;
  const desired = { x: (dx / d) * b.maxSpeed, y: (dy / d) * b.maxSpeed };
  return steerToward(desired, b.vel, CURSOR_FORCE * t);
}

export function orbitBias(b: Bubble, anchors: Rect[]): Vec2 {
  if (anchors.length === 0) return { x: 0, y: 0 };
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < anchors.length; i++) {
    const r = anchors[i];
    if (!r) continue;
    cx += r.x + r.w / 2;
    cy += r.y + r.h / 2;
  }
  cx /= anchors.length;
  cy /= anchors.length;
  const dx = cx - (b.pos.x + b.size / 2);
  const dy = cy - (b.pos.y + b.size / 2);
  const d = Math.hypot(dx, dy) || 1;
  return { x: (-dy / d) * ORBIT_FORCE, y: (dx / d) * ORBIT_FORCE };
}
